import React from 'react';
import { Col, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { Field, FormikProvider, useFormik } from 'formik';
import { initialProjectDocument, ProjectDocumentsModel } from 'app/edozvola/cases/project-documents/model/project-documents.model';
import { projectDocumentValidationSchema } from 'app/edozvola/cases/project-documents/model/project-documents.schema';
import { Button, Input, Modal } from 'edozvola-frontend-lib/dist/ui/components';
import { FormikDatepicker } from 'edozvola-frontend-lib/dist/ui/components/datepicker/FormikDatepicker';
import { APP_DATE_FORMAT } from 'app/config/constants';
import OibInput from 'app/shared/oib/oib-input';
import { LegalEntityModel } from 'app/shared/oib/oib.model';
import AddressInput from 'app/shared/address-input/address-input';
import { ChamberMemberCodebookModel } from 'app/shared/model/shared.model';
import { fetchProjectDocumentQuery, saveProjectDocumentQuery } from 'app/edozvola/cases/project-documents/service/project-document.queries';
import { useParams } from 'react-router-dom';
import { Loader } from 'app/shared/loader/loader';
import { Label } from 'edozvola-frontend-lib/dist/ui';
import {
  fetchChamberMembersQuery,
  fetchProjectMapQuery,
  fetchProjectTypeQueryForCaseCategory,
  fetchSubjectTypeQuery,
} from 'app/edozvola/codebooks/codebooks.queries';
import { IRootState } from 'app/config/store';
import { useSelector } from 'react-redux';
import { PaginatedDropdown } from 'edozvola-frontend-lib/dist/ui/components/paginated-dropdown';
import { SUBJECT_TYPE_ENUM } from 'app/shared/enumeration/enums';
import { formatAddress } from 'app/shared/address-input/address-helper';
import moment from "moment";

export interface IProjectDocumentFormProps {
  projectDocumentId: string | null;
  setProjectDocumentId: any;
  readOnly: boolean;
}

const ProjectDocumentsForm = (props: IProjectDocumentFormProps) => {
  const { id: uuid } = useParams<{ id: string }>();
  const selectedCase = useSelector((state: IRootState) => state.caseEntity);

  const { data: projectTypeOptions, isLoading: projectTypeOptionsLoading } = fetchProjectTypeQueryForCaseCategory(selectedCase.idCase);
  const { data: projectMapOptions, isLoading: projectMapOptionsLoading } = fetchProjectMapQuery();
  const { data: chamberMemberOptions, isLoading: chamberMemberOptionsLoading } = fetchChamberMembersQuery();
  const { data: subjectTypeOptions, isLoading: subjectTypeOptionsLoading } = fetchSubjectTypeQuery();
  const { data: projectDocumentData, isLoading: projectDocumentDataLoading } = fetchProjectDocumentQuery(props.projectDocumentId);

  const formik = useFormik({
    initialValues: projectDocumentData || initialProjectDocument,
    validationSchema: projectDocumentValidationSchema,
    onSubmit: (values: ProjectDocumentsModel) => saveProjectDocumentMutation(values),
    enableReinitialize: true,
    validateOnMount: true,
    initialTouched: {
      designerOffice: {
        legalEntity: {
          oib: true,
        },
      },
    },
  });

  const { mutate: saveProjectDocumentMutation, isPending: isProjectDocumentationSaving } = saveProjectDocumentQuery(uuid, () =>
    closeModal(),
  );

  const filteredSubjectTypeOptions = React.useMemo(() => {
    return subjectTypeOptions?.filter((opt: any) => opt.id !== SUBJECT_TYPE_ENUM.FIZICKA_OSOBA && opt.id !== SUBJECT_TYPE_ENUM.OPG);
  }, [subjectTypeOptions]);

  const oibFetched = (fetchedOibData: LegalEntityModel) => {
    formik.setFieldValue('designerOffice.oibFetched', true);
    formik.setFieldValue('designerOffice.legalEntity', fetchedOibData);
  };

  const onOibChanged = (value: string) => {
    formik.setTouched({
      ...formik.touched,
      designerOffice: {
        legalEntity: {
          oib: true,
        },
      },
    });
    formik.setFieldValue('designerOffice.oibFetched', false);
    formik.setFieldValue('designerOffice.legalEntity.companyName', '');
    formik.setFieldValue('designerOffice.legalEntity.address', null);
    formik.setFieldValue('designerOffice.legalEntity.oib', value);
  };

  const onChamberMemberChanged = (selected: ChamberMemberCodebookModel[]) => {
    formik.setValues({
      ...formik.values,
      authorizedDesigner: {
        authorizationNumber: selected,
        oib: selected[0] ? selected[0].oib : '',
      },
      authorizationNumberExists: true,
    });
  };

  const closeModal = () => {
    props.setProjectDocumentId(null);
  };

  const onSubjectTypeChange = selected => {
    onOibChanged('');
    formik.setFieldValue('designerOffice.legalEntity.mb', '');
    formik.setFieldValue('designerOffice.subjectType', selected);

    setTimeout(() => {
      formik.validateForm();
    }, 0);
  };

  const isOsobaSelectedEqual = (subjectType: SUBJECT_TYPE_ENUM) =>
    formik.values.designerOffice?.subjectType &&
    formik.values.designerOffice?.subjectType[0] &&
    formik.values.designerOffice?.subjectType[0].id === subjectType;

  const isPravnaOsobaSelected = isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.PRAVNA_OSOBA);
  const isObrtOrSlobodnaDjelatnostSelected =
    isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.OBRT) || isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.SLOBODNA_DJELATNOST);

  const phoneChanged = (event: any, field: string) => {
    formik.setFieldValue(field, event.target.value);
    event.preventDefault();
  };

  return (
    <>
      {projectDocumentDataLoading && <Loader />}
      {!projectDocumentDataLoading && (
        <Modal scrollable isOpen={props.projectDocumentId !== null} size={'xl'}>
          {props.readOnly ? (
            <ModalHeader toggle={() => closeModal()}>Pregled projekta</ModalHeader>
          ) : (
            <ModalHeader>{props.projectDocumentId === '' ? 'Unos projekta' : 'Uređivanje projekta'}</ModalHeader>
          )}
          <ModalBody>
            <FormikProvider value={formik}>
              <div className={'d-flex justify-content-between flex-column gap-3'}>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Vrsta projekta'} />
                    <PaginatedDropdown
                      placeholder={!props?.readOnly && 'Odaberi vrstu projekta'}
                      onItemSelect={selected => formik.setFieldValue('projectType', selected)}
                      selected={formik.values.projectType}
                      labelKey={'value'}
                      options={projectTypeOptionsLoading ? [] : projectTypeOptions}
                      disabled={props?.readOnly}
                      error={(formik.errors.projectType as string) ?? ''}
                      showErrorMessage
                    />
                  </Col>
                  <Col md={6}>
                    <Label labelText={'Oznaka projekta'} />
                    <Input
                      disabled={props.readOnly}
                      value={formik.values.projectMark}
                      error={formik.errors.projectMark ? `${formik.errors.projectMark}` : ''}
                      maxLength={100}
                      onChange={event => formik.setFieldValue('projectMark', event.target.value)}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Dodatni opis'} />
                    <Input
                      disabled={props.readOnly}
                      value={formik.values.additionalDescription}
                      error={formik.errors.additionalDescription ? `${formik.errors.additionalDescription}` : ''}
                      onChange={event => formik.setFieldValue('additionalDescription', event.target.value)}
                    />
                  </Col>
                  <Col md={6}>
                    <Label labelText={'Datum izrade'} />
                    <Field
                      id="projectCreatedDate"
                      name="projectCreatedDate"
                      render={({ field }) => (
                        <FormikDatepicker
                          field={field}
                          disabled={props.readOnly}
                          setFieldValue={formik.setFieldValue}
                          dateFormat={APP_DATE_FORMAT}
                          maxDate={moment()}
                          error={formik.errors.projectCreatedDate ? `${formik.errors.projectCreatedDate}` : ''}
                        />
                      )}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Mapa'} />
                    <PaginatedDropdown
                      placeholder={!props?.readOnly && 'Odaberi mapu'}
                      onItemSelect={selected => formik.setFieldValue('projectMap', selected)}
                      selected={formik.values.projectMap}
                      labelKey={'value'}
                      options={projectMapOptionsLoading ? [] : projectMapOptions}
                      disabled={props?.readOnly}
                      clearButton
                      error={formik.errors.projectMap as string}
                      showErrorMessage
                    />
                  </Col>
                </Row>
                <hr />
                <div className={'form-header'}>Ovlašteni projektant</div>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Broj ovlaštenja/Ime i prezime'} />
                    <PaginatedDropdown
                      placeholder={!props?.readOnly && 'Odaberi broj ovlaštenja/ime i prezime'}
                      onItemSelect={onChamberMemberChanged}
                      selected={formik.values.authorizedDesigner?.authorizationNumber}
                      labelKey={'value'}
                      options={chamberMemberOptionsLoading ? [] : chamberMemberOptions}
                      disabled={props?.readOnly}
                      error={
                        formik.errors.authorizedDesigner && formik.errors.authorizedDesigner['authorizationNumber']
                          ? (formik.errors.authorizedDesigner['authorizationNumber'] as string)
                          : ''
                      }
                      showErrorMessage
                    />
                  </Col>
                  <Col md={6}>
                    <Label labelText={'OIB'} />
                    <Input disabled value={formik.values.authorizedDesigner?.oib} />
                  </Col>
                </Row>
                <hr />
                <div className={'form-header'}>Projektni ured</div>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Vrsta subjekta'} />
                    <PaginatedDropdown
                      placeholder={!props?.readOnly && 'Odaberi broj ovlaštenja/ime i prezime'}
                      onItemSelect={onSubjectTypeChange}
                      selected={formik.values.designerOffice?.subjectType}
                      labelKey={'value'}
                      options={filteredSubjectTypeOptions}
                      disabled={props.readOnly}
                      error={(formik.errors.designerOffice?.subjectType as string) ?? ''}
                      showErrorMessage
                    />
                  </Col>
                  <Col md={6}>
                    {isPravnaOsobaSelected ? (
                      <OibInput
                        disabled={props.readOnly}
                        value={formik.values.designerOffice.legalEntity?.oib}
                        handleBlur={formik.handleBlur}
                        handleChange={event => onOibChanged(event.target.value)}
                        errors={formik.errors.designerOffice ? formik.errors.designerOffice['legalEntity'] : {}}
                        touched={formik.touched.designerOffice['legalEntity']}
                        onOibFetched={oibFetched}
                        fetchLegalEntity
                      />
                    ) : (
                      isObrtOrSlobodnaDjelatnostSelected && (
                        <>
                          <Label labelText="MB" />
                          <Input
                            value={formik.values.designerOffice?.legalEntity?.mb}
                            id="mb"
                            name="mb"
                            maxLength={11}
                            disabled={props.readOnly}
                            onChange={event => formik.setFieldValue('designerOffice.legalEntity.mb', event.target.value)}
                            error={
                              formik.errors.designerOffice?.legalEntity && formik.errors.designerOffice?.legalEntity['mb']
                                ? formik.errors.designerOffice?.legalEntity['mb']
                                : ''
                            }
                          />
                        </>
                      )
                    )}
                  </Col>
                </Row>

                {(isPravnaOsobaSelected || isObrtOrSlobodnaDjelatnostSelected) && (
                  <Row>
                    <Col md={6}>
                      <Label labelText={'Naziv'} />
                      <Input
                        disabled={props.readOnly || isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.PRAVNA_OSOBA)}
                        value={formik.values.designerOffice.legalEntity?.companyName}
                        onChange={event => formik.setFieldValue('designerOffice.legalEntity.companyName', event.target.value)}
                        error={
                          formik.errors.designerOffice?.legalEntity && formik.errors.designerOffice?.legalEntity['companyName']
                            ? formik.errors.designerOffice?.legalEntity['companyName']
                            : ''
                        }
                        maxLength={200}
                      />
                    </Col>
                  </Row>
                )}

                {isPravnaOsobaSelected && formik.values.designerOffice?.legalEntity?.address && (
                  <Row>
                    <Col md={6}>
                      <Label labelText="Adresa iz OIB-a" />
                      <span>{formatAddress(formik.values.designerOffice?.legalEntity.address)}</span>
                    </Col>
                  </Row>
                )}
                <AddressInput
                  readOnly={props.readOnly}
                  field={formik.values.designerOffice.address}
                  errors={formik.errors.designerOffice ? formik.errors.designerOffice['address'] : {}}
                  setFieldValue={(field: string, value: any) => formik.setFieldValue(`designerOffice.address.${field}`, value)}
                />

                <Row>
                  <Col md={6}>
                    <Label labelText={'Email'} />
                    <Input
                      value={formik.values?.email}
                      id={'email'}
                      name={'email'}
                      error={formik.errors?.email ?? ''}
                      disabled={props.readOnly}
                      maxLength={60}
                      onChange={event => formik.setFieldValue(`email`, event.target.value)}
                    />
                  </Col>
                  <Col md={6}>
                    <Label labelText={'Telefon'} />
                    <Input
                      value={formik.values?.telephoneNumber}
                      id={'telephoneNumber'}
                      name={'telephoneNumber'}
                      disabled={props.readOnly}
                      maxLength={50}
                      onChange={e => phoneChanged(e, 'telephoneNumber')}
                    />
                  </Col>
                </Row>
              </div>
            </FormikProvider>
          </ModalBody>
          <ModalFooter>
            {!props.readOnly && (
              <>
                <Button label={'Odustani'} outline size={'lg'} onClick={closeModal} />
                <Button
                  label={'Spremi'}
                  size={'lg'}
                  disabled={!formik.dirty || !formik.isValid}
                  isLoading={isProjectDocumentationSaving}
                  color={'info'}
                  onClick={() => formik.handleSubmit()}
                />
              </>
            )}
          </ModalFooter>
        </Modal>
      )}
    </>
  );
};

export default ProjectDocumentsForm;
